import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { PieDonut } from '../../components/charts/Charts.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { formatMoney, formatDuration, formatNumber } from '../../lib/format.js';

/**
 * Where the money goes, by kind of destination.
 *
 * The question behind a phone bill is almost never "how many calls" — it is
 * "why is it that much", and the answer is nearly always one category. Mobile
 * at three times the landline rate, or a handful of international calls
 * outweighing a thousand local ones.
 *
 * Cost per minute sits next to total spend deliberately: a category can be
 * small in total and alarming per minute, and that is the one worth catching
 * before it grows.
 */

interface Row {
  class: string;
  label: string;
  calls: number;
  billable_sec: number;
  spend: string;
  share_pct: number;
  cost_per_min: string | null;
  top_countries: Array<{ iso2: string; calls: number }>;
}

/** Stable per category, so the donut and the table agree at a glance. */
const HUE: Record<string, string> = {
  mobile: 'var(--arc-c1)',
  landline: 'var(--arc-c2)',
  international: 'var(--arc-c4)',
  voip: 'var(--arc-c3)',
  share_call: 'var(--arc-c5)',
  premium: 'var(--arc-danger)',
  toll_free: 'var(--arc-fg-4)',
  on_net: 'var(--arc-fg-3)',
  unknown: 'var(--arc-border-strong)',
};

export function SpendBreakdown({ tenantId, days = 30, bare = false }: {
  tenantId: string; days?: number;
  /** Inside another tile, which supplies the card and the heading. */
  bare?: boolean;
}) {
  const q = useQuery({
    queryKey: ['cdr-breakdown', tenantId, days],
    queryFn: () => api.get<{
      currency: string; total_spend: string; breakdown: Row[];
    }>(`/v1/tenants/${tenantId}/cdrs/breakdown?days=${days}`),
    enabled: !!tenantId,
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const rows = (q.data?.breakdown ?? []).filter((r) => r.class !== 'on_net');
  const currency = q.data?.currency ?? 'ZAR';
  const total = Number(q.data?.total_spend ?? 0);

  // Premium and share-call are where toll fraud shows up on a bill first.
  const risky = rows.filter((r) => r.class === 'premium' || r.class === 'share_call');
  const dearest = [...rows]
    .filter((r) => r.cost_per_min !== null)
    .sort((a, b) => Number(b.cost_per_min) - Number(a.cost_per_min))[0];

  return (
    <div className={bare ? undefined : 'arc-card'}>
      {!bare && (
        <div className="arc-card-head">
          <h2 className="arc-h2">What the spend is made of</h2>
          <span className="arc-subtle">outbound, last {days} days</span>
        </div>
      )}

      <div className={bare ? undefined : 'arc-card-body'}>
        {rows.length === 0 ? (
          <p className="arc-muted" style={{ fontSize: 12.5, margin: 0 }}>
            No billable outbound calls in this window.
          </p>
        ) : (
          <>
            {risky.length > 0 && (
              <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
                <span>
                  {risky.map((r) => `${r.label}: ${formatMoney(r.spend, currency)}`).join(', ')}
                  {' '}— premium and share-call ranges are where toll fraud shows up first.
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <PieDonut
                data={rows.map((r) => ({
                  label: r.label,
                  value: Number(r.spend),
                  color: HUE[r.class] ?? 'var(--arc-fg-4)',
                }))}
                size={150}
              />

              {/* Wrapped so a narrow window scrolls the table, not the page. */}
              <div className="arc-table-wrap" style={{ flex: 1, minWidth: 232 }}>
                <table className="arc-table">
                  <thead>
                    {/* Three columns, each carrying its own explanation
                        underneath. Five columns of numbers do not fit beside a
                        chart at half width, and the pairs belong together
                        anyway: minutes explain the call count, and the rate
                        explains the amount. */}
                    <tr>
                      <th>Destination</th>
                      <th className="arc-td-num">Calls</th>
                      <th className="arc-td-num">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.class}>
                        <td>
                          <div className="arc-row" style={{ gap: 8 }}>
                            <span className="arc-swatch"
                              style={{ background: HUE[r.class] ?? 'var(--arc-fg-4)' }} />
                            <div className="arc-stack">
                              <span style={{ fontWeight: 550 }}>{r.label}</span>
                              {r.top_countries.length > 0 && (
                                <span className="arc-subtle" style={{ fontSize: 11 }}>
                                  {r.top_countries.map((c) => c.iso2).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="arc-td-num">
                          <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                            <span className="arc-muted">{formatNumber(r.calls)}</span>
                            <span className="arc-subtle" style={{ fontSize: 11 }}>
                              {formatDuration(r.billable_sec)}
                            </span>
                          </div>
                        </td>
                        <td className="arc-td-num">
                          <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                            <span style={{ fontWeight: 600 }}>
                              {formatMoney(r.spend, currency)}
                              <span className="arc-subtle" style={{ marginLeft: 5, fontWeight: 400 }}>
                                {r.share_pct}%
                              </span>
                            </span>
                            <span className="arc-subtle" style={{ fontSize: 11 }}>
                              {r.cost_per_min ? `${formatMoney(r.cost_per_min, currency)}/min` : '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ fontWeight: 600 }}>Total</td>
                      <td className="arc-td-num" style={{ fontWeight: 600 }}>
                        {formatMoney(String(total), currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                {dearest && (
                  <p className="arc-muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
                    Dearest per minute: <strong>{dearest.label}</strong> at{' '}
                    {formatMoney(dearest.cost_per_min!, currency)}/min
                    {dearest.share_pct < 20 && (
                      <> — only {dearest.share_pct}% of spend, but the rate to watch if it grows</>
                    )}
                    .
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
